import { useCallback } from 'react';

/**
 * Hook for Excel-like arrow key navigation in invoice tables.
 * Usage: onKeyDown={handleTableKeyDown} on each input/select in the table.
 * Inputs need data-row and data-col attributes.
 */
export const useTableNavigation = (tableTestId) => {
  const handleTableKeyDown = useCallback((e) => {
    const { key } = e;
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return;

    const el = e.target;
    const row = parseInt(el.dataset.row);
    const col = parseInt(el.dataset.col);
    if (isNaN(row) || isNaN(col)) return;

    // Don't navigate if inside a select (let native behavior work for up/down)
    if (el.tagName === 'SELECT' && (key === 'ArrowUp' || key === 'ArrowDown')) return;

    // For text inputs, only navigate left/right when cursor is at boundary
    if (el.tagName === 'INPUT' && el.type !== 'number') {
      if (key === 'ArrowLeft' && el.selectionStart > 0) return;
      if (key === 'ArrowRight' && el.selectionStart < el.value.length) return;
    }

    e.preventDefault();

    let nextRow = row, nextCol = col;
    if (key === 'ArrowUp') nextRow = row - 1;
    if (key === 'ArrowDown') nextRow = row + 1;
    if (key === 'ArrowLeft') nextCol = col - 1;
    if (key === 'ArrowRight') nextCol = col + 1;

    const table = document.querySelector(`[data-testid="${tableTestId}"]`);
    if (!table) return;

    const next = table.querySelector(`[data-row="${nextRow}"][data-col="${nextCol}"]`);
    if (next) {
      next.focus();
      if (next.tagName === 'INPUT' && next.type === 'number') next.select();
    }
  }, [tableTestId]);

  return handleTableKeyDown;
};
